import type { AppError } from './AppError';
import { isAppError } from './AppError';
import { USER_MESSAGE_KEYS } from './errorCodes';
import { normalizeError } from './normalize';

export interface ResolvedErrorCopy {
  title: string;
  hint?: string;
  code: string;
  category: string;
  severity: AppError['severity'];
  presentation: AppError['presentation'];
}

/**
 * Resolve user-facing copy from an AppError or legacy validation key.
 * Falls back to generic plain-language text — never exposes SDK messages.
 */
export function resolveErrorCopy(
  error: unknown,
  t?: Record<string, string> | null,
): ResolvedErrorCopy {
  const appErr = isAppError(error) ? error : normalizeError(error);
  const dict = t ?? {};
  const title = dict[appErr.userMessageKey]
    ?? (appErr.userMessageKey.startsWith('error_') ? dict[appErr.userMessageKey] : undefined)
    ?? dict[USER_MESSAGE_KEYS.GENERIC]
    ?? 'Something went wrong.';
  const hint = appErr.hintKey ? dict[appErr.hintKey] : undefined;

  return {
    title,
    hint,
    code: appErr.code,
    category: appErr.category,
    severity: appErr.severity,
    presentation: appErr.presentation,
  };
}

/**
 * One-line user-facing error for forms/hooks. Never returns raw Firebase codes
 * like `INTERNAL` / `functions/internal`.
 */
export function formatCaughtError(
  error: unknown,
  t?: Record<string, string> | null,
): string {
  const copy = resolveErrorCopy(error, t);
  if (copy.hint && copy.hint !== copy.title) return `${copy.title} ${copy.hint}`;
  return copy.title;
}

/** Legacy validation keys and new err_* keys share the same lookup. */
export function resolveInlineErrorKey(error: unknown, t: Record<string, string>): string {
  return resolveErrorCopy(error, t).title;
}
